import { UseFormReturn, Controller } from "react-hook-form"
import { TextInput } from "../EditCampForm/EditCampForm"
import RichTextInput from "../../../ui/RichTextInput"
import SelectInput, { Option } from "../../../ui/SelectInput"
import CheckboxWithLabelForForm from "../../../ui/CheckboxWithLabelForForm"
import SaveButtons from "../SaveButtons"
import { AutomatedMessageType } from "./validation"
import useAutomatedMessageForm from "./useAutomatedMessagesForm"
import ProcessingState from "~/components/ui/Admin/ProcessingState"
import useSendTestMessage from "../hooks/useSendTestMessage"
import { useState } from "react"
import { useMe } from "~/api2/hooks/useMe"
import { useCamp } from "~/api2/hooks/useCamp"

const AutomatedMessagesForm = () => {
  const { onSubmit, reactHookForm, handleSetDefaultValues, isPendingSaveAutomatedMessages, isLoadingData, loadedDone } =
    useAutomatedMessageForm()

  if (isLoadingData) return <ProcessingState loadingMessages={[]} successMessage={""} />

  return (
    //TODO: I had to add an hack to make the richtext reactive, for some reason internally he is not
    // changing when the props change after initial mount, the component itself should be revised
    <form key={JSON.stringify(loadedDone)} onSubmit={reactHookForm.handleSubmit(onSubmit)}>
      <SaveButtons
        isDirty={reactHookForm.formState?.isDirty}
        errors={reactHookForm.formState?.errors}
        handleCancel={handleSetDefaultValues}
        isLoading={isPendingSaveAutomatedMessages}
      />

      <div className="pb-20">
        <div className="my-6">
          <div className="text-lgxl font-semibold text-gray-900 mb-1">Automated Messages</div>
          <div className="text-gray-500 font-medium">
            Send messages automatically before check-in and after check-out
          </div>
        </div>

        <MessageSection
          title="Before check-in"
          subject="before_checkin_subject"
          content="before_checkin_content"
          sendTiming="minutes_before_checkin"
          isBeforeOption={true}
          reactHookForm={reactHookForm}
        />

        <div className="mb-9"></div>

        <MessageSection
          title="After check-out"
          subject="after_checkout_subject"
          content="after_checkout_content"
          sendTiming="minutes_after_checkout"
          isBeforeOption={false}
          reactHookForm={reactHookForm}
        />
      </div>
    </form>
  )
}

interface MessageSectionProps {
  title: string
  subject: keyof AutomatedMessageType
  content: keyof AutomatedMessageType
  sendTiming: keyof AutomatedMessageType
  isBeforeOption?: boolean
  reactHookForm: UseFormReturn<AutomatedMessageType>
}

const MessageSection = ({
  title,
  subject,
  content,
  sendTiming,
  isBeforeOption = true,
  reactHookForm,
}: MessageSectionProps) => {
  const beforeOrAfter = isBeforeOption ? "before" : "after"
  const sendTimingValue = reactHookForm.watch(sendTiming) as number
  const isDisabledInput = sendTimingValue === null
  const [isSending, setIsSending] = useState(false)
  const { data: user } = useMe()
  const { index: { data: camp }} = useCamp()

  const currentUserEmail = user?.email
  const campId = camp?.id

  const mutation = useSendTestMessage()

  const handleSendTest = () => {
    mutation.mutate({
      campId: campId.toString(),
      subject: reactHookForm.watch(subject) as string,
      content: reactHookForm.watch(content) as string,
      sendTiming: sendTimingValue,
      isBeforeCheckin: isBeforeOption,
    })
  }

  const options = [
    { label: `Not sending`, value: null },
    { label: `1 day ${beforeOrAfter}`, value: 1440 },
    { label: `2 days ${beforeOrAfter}`, value: 2880 },
    { label: `3 days ${beforeOrAfter}`, value: 4320 },
    { label: `4 days ${beforeOrAfter}`, value: 5760 },
    { label: `5 days ${beforeOrAfter}`, value: 7200 },
    { label: `6 days ${beforeOrAfter}`, value: 8640 },
    { label: `1 week ${beforeOrAfter}`, value: 10080 },
    { label: `2 weeks ${beforeOrAfter}`, value: 20160 },
    { label: `At time of check-in`, value: 0 },
    { label: `1 hour ${beforeOrAfter}`, value: 60 },
    { label: `2 hours ${beforeOrAfter}`, value: 120 },
    { label: `4 hours ${beforeOrAfter}`, value: 240 },
    { label: `6 hours ${beforeOrAfter}`, value: 360 },
    { label: `12 hours ${beforeOrAfter}`, value: 720 },
  ]

  return (
    <div className="">
      <div className="flex justify-between items-center">
        <div className="font-bold text-lg mb-1.5">{title}</div>
        <button
          type="button"
          onClick={handleSendTest}
          disabled={isDisabledInput || isSending}
          className="-mb-3 px-3 py-2 text-sm/5 font-medium text-white bg-blue-600 rounded-md hover:enabled:bg-blue-700 disabled:opacity-50"
        >
          <div>{isSending ? "Sending..." : "Send Test Email"}</div>
          <div className="text-xs italic opacity-80">to {currentUserEmail}</div>
        </button>
      </div>
      <div className="text-md mb-1 font-semibold">Sending</div>

      <Controller
        name={sendTiming}
        control={reactHookForm.control}
        render={({ field }) => (
          <SelectInput
            options={options}
            selected={options.find((o) => o.value === field.value)}
            setSelected={(o) => field.onChange((o as Option).value)}
            className="max-w-sm"
          />
        )}
      />

      <div className="mb-3"></div>

      <SendingForm
        isBeforeCheckin={isBeforeOption}
        sendTiming={sendTiming}
        subject={subject}
        content={content}
        reactHookForm={reactHookForm}
        isDisabledInput={isDisabledInput}
      />
    </div>
  )
}

const SendingForm = ({
  subject,
  content: contentFieldName,
  sendTiming: sendTimingField,
  reactHookForm,
  isBeforeCheckin,
  isDisabledInput
}: {
  subject: keyof AutomatedMessageType
  content: keyof AutomatedMessageType
  sendTiming: keyof AutomatedMessageType
  reactHookForm: UseFormReturn<AutomatedMessageType>
  isBeforeCheckin: boolean
  isDisabledInput: boolean
}) => {
  return (
    <div className={isDisabledInput ? "opacity-50" : ""}>
      <div className="mb-3">
        <div className="block font-semibold text-md text-gray-900 mb-0.5">Email subject</div>
        <Controller
          name={subject}
          control={reactHookForm?.control}
          render={({ field }) => (
            <TextInput
              field={field.name}
              inputClassName={"border border-gray-300 w-full rounded-md"}
              disabled={isDisabledInput}
              label={""}
              control={reactHookForm.control}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="mb-3 relative">
        <div className="block font-semibold text-md text-gray-900 mb-0.5">Email content</div>
        <Controller
          name={contentFieldName}
          control={reactHookForm.control}
          render={({ field }) => (
            <RichTextInput
              optionalVariablesDropBox={optionalVariablesDropBox}
              value={field.value}
              setValue={(v) => !isDisabledInput && field.onChange(v)}
              hyperlink
            />
          )}
        />
        {isDisabledInput && <div className="absolute w-full h-full inset-0 z-50"></div>}
      </div>

      <div className="relative">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Controller
            name={`${isBeforeCheckin ? "before_checkin" : "after_checkout"}_cc_camp_notification_emails`}
            control={reactHookForm?.control}
            render={({ field }) => (
              <CheckboxWithLabelForForm
                name={field.name}
                label="CC Camp email"
                value={!!field.value}
                setValue={(value) => {
                  field.onChange(value)
                }}
                disabled={isDisabledInput}
              />
            )}
          />
        </div>
      </div>
    </div>
  )
}

export default AutomatedMessagesForm

const VARIABLE_OPTIONS: Record<string, string> = {
  "First Name": "{{ first_name }}",
  "Last Name": "{{ last_name }}",
  "Reservation confirmation link": "{{ reservation_confirmation_link }}",
  "Start Date": "{{ start_date }}",
  "End Date": "{{ end_date }}",
  "Checkin Policy": "{{ checkin_policy }}",
  "Site Name": "{{ site_name }}",
  "Total Owed": "{{ total_owed }}",
  "Total Paid": "{{ total_paid }}",
}

const optionalVariablesDropBox = Object.entries(VARIABLE_OPTIONS).map(([label, value]) => ({
  label,
  value,
}))
